import { useEffect, useRef } from "react";
import { usePiegeFocus } from "../guide/usePiegeFocus.js";
import { etatInfos } from "./etatInfos.js";
import { ContenuCredits } from "./ContenuCredits.js";
import { IconeChevronGauche, IconeFermer } from "./icones.js";
import { empiler } from "./retourSysteme.js";
import { useClavierSurcouche } from "./useClavierSurcouche.js";
import { useInfos } from "./useInfos.js";
import * as s from "./infosStyles.js";
import "./infos.css";

/**
 * ============================================================================
 *  CreditsPanel.tsx — Les crédits
 * ----------------------------------------------------------------------------
 *  Référence : `sources/design/design_handoff_credits/`. Sur ordinateur, une fenêtre
 *  centrée de 480 px sur un voile flouté ; sur téléphone, une vue plein écran qui arrive
 *  par la droite, avec « ‹ Retour » (vers la feuille) et × (tout fermer).
 *
 *  Le contenu — logo, deux lignes de crédits, signature, bouton lien — vient de
 *  `chaines.json` (clés `info*`). La voix off est mise en pause par l'OUVERTURE (voir
 *  `logiqueInfos`), pas par ce composant : il ne fait qu'afficher.
 *
 *  Modal : `aria-modal`, focus piégé sur le panneau puis rendu à ce qui l'a ouvert,
 *  Échap ferme tout.
 * ==========================================================================*/

export function CreditsPanel({ assetBaseUrl }: { assetBaseUrl: string }) {
  const infos = useInfos();
  const { t, mobile } = infos;
  const racine = useRef<HTMLDivElement>(null);
  const fermer = useRef<HTMLButtonElement>(null);

  // Le bouton Retour d'Android revient à la feuille (« ‹ Retour »), comme le bouton de l'écran.
  const retour = useRef(infos.retourCredits);
  retour.current = infos.retourCredits;
  useEffect(() => (mobile ? empiler(() => retour.current()) : undefined), [mobile]);

  usePiegeFocus(
    racine,
    true,
    () => fermer.current,
    // Ordinateur : le menu s'est fermé, on revient au bouton. Mobile : la feuille est encore là, on revient à sa ligne.
    () => (mobile ? document.querySelector<HTMLElement>('[data-inf-ligne="credits"]') : etatInfos.lire().origine),
  );

  useClavierSurcouche(true, (event) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    infos.fermerTout();
  });

  const contenu = <ContenuCredits t={t} assetBaseUrl={assetBaseUrl} surLien={infos.lien} tailleLogo={mobile ? 132 : 148} />;

  if (mobile) {
    return (
      <div
        ref={racine}
        role="dialog"
        aria-modal="true"
        aria-label={t("infoCredits")}
        className="inf-credits-plein"
        style={s.creditsPlein}
      >
        <div style={s.entetePlein}>
          <button type="button" className="inf-bouton" onClick={infos.retourCredits} style={s.boutonRetour}>
            <IconeChevronGauche taille={20} />
            {t("infoRetour")}
          </button>
          <button ref={fermer} type="button" className="inf-croix" aria-label={t("infoFermer")} onClick={infos.fermerTout} style={s.croix}>
            <IconeFermer />
          </button>
        </div>
        {contenu}
      </div>
    );
  }

  return (
    <div className="inf-voile" onClick={infos.fermerTout} style={s.creditsVoile}>
      <div
        ref={racine}
        role="dialog"
        aria-modal="true"
        aria-label={t("infoCredits")}
        className="inf-credits-fenetre"
        // Un clic dans la fenêtre ne doit pas atteindre le voile, qui ferme.
        onClick={(event) => event.stopPropagation()}
        style={s.creditsFenetre}
      >
        <button ref={fermer} type="button" className="inf-croix" aria-label={t("infoFermer")} onClick={infos.fermerTout} style={s.croixFenetre}>
          <IconeFermer />
        </button>
        {contenu}
      </div>
    </div>
  );
}
